<template>
<select class="wn-choices"
    ref="main"
    @change="handleValue"
    :multiple="multiple">
</select>
</template>

<script>
// generated by claude - props/events named to match vue-select's contract (options, v-model,
// multiple, taggable, clearable, filterable, label, @search(search, loading), @option:created)
// on purpose: WnChoices is a choices.js-backed stand-in for vue-select, needed because vue-select@4
// (the Vue-3-compatible major) is still beta. Swapping back to vue-select later, once it's stable,
// should only mean renaming the tag at each call site - don't add props/events here that vue-select
// doesn't also have, and don't rename the ones that match it.
export default {
    emits: ['input', 'search', 'option:created'],
    props: {
        options: {
            type: Array,
            required: true,
            default: () => ([]),
        },
        value: {
            required: false,
        },
        multiple: {
            type: Boolean,
            reguired: false,
            default: false,
        },
        settings: {
            type: Object,
            required: false,
            default: () => ({}),
        },
        placeholder: {
            type: String,
            required: false,
        },
        label: {
            // which key to read the display text from, when options are objects (ignored for
            // plain-string options). Matches vue-select's `label` prop; default kept at 'text'
            // so existing consumers (CountrySelect/IndustrySelect) are unaffected.
            type: String,
            required: false,
            default: 'text',
        },
        taggable: {
            // allow creating a new option that isn't in `options` by typing it and hitting enter
            type: Boolean,
            required: false,
            default: false,
        },
        clearable: {
            // show a remove button on the selected item(s)
            type: Boolean,
            required: false,
            default: false,
        },
        filterable: {
            // false = don't filter `options` locally as the user types; use together with
            // @search to let the parent drive the option list from a remote search instead
            type: Boolean,
            required: false,
            default: true,
        },
    },
    data: () => ({
        instance: null,
        loading: false,
    }),
    watch: {
        // generated by claude - only `options` is watched (not `value`): a user-driven selection
        // already updates choices.js's own visual state immediately, re-running setChoices for that
        // too would re-trigger the exact dropdown-closes-while-typing bug this fixes.
        options: {
            deep: true,
            handler() {
                this.$nextTick(() => this.updateChoices())
            }
        },
    },
    computed: {
        // generated by claude - vue-select can show a selected value that isn't in `options`
        // (e.g. right after an async-search reset); a native <select> can't unless a matching
        // choice exists, so splice the current value(s) in when missing.
        displayOptions() {
            if (!this.value) return this.options;

            const selected = this.multiple ? this.value : [this.value];
            const missing = selected.filter((v) => v != null && !this.options.some((o) => this.optionId(o) == this.optionId(v)));

            return missing.length ? [...this.options, ...missing] : this.options;
        },
    },
    methods: {
        // generated by claude - options can be plain strings (e.g. share tags) or {id, text}-shaped
        // objects (e.g. countries); these two helpers are the only place that needs to know which
        optionId(option) {
            return typeof option === 'object' && option !== null ? option.id : option
        },
        optionLabel(option) {
            return typeof option === 'object' && option !== null ? option[this.label] : option
        },
        // generated by claude - choices.js' own {value, label} shape for setChoices()/the choices
        // config option. value is always stringified so later comparisons don't fall into the
        // number-vs-string trap (this is what broke single-select matching before).
        buildChoicesArray() {
            const selectedIds = (this.multiple ? (this.value || []) : (this.value != null ? [this.value] : []))
                .map((v) => String(this.optionId(v)));

            const choices = this.displayOptions.map((option) => {
                const id = String(this.optionId(option));

                return { value: id, label: this.optionLabel(option), selected: selectedIds.includes(id) };
            });

            // a multi-select has no "no value" choice: a selected placeholder would render as a real pill in an empty field
            if (this.multiple) return choices;

            const placeholderLabel = this.placeholder || this.$_locale('choicesjs.noValue');
            const placeholderChoice = { value: '', label: placeholderLabel, selected: selectedIds.length === 0 };

            return [placeholderChoice, ...choices];
        },
        initChoices() {
            if (!window.Choices) {
                console.warn('ChoicesJS not loaded!')
                return;
            }
            const { main } = this.$refs

            const choicesSettings = {
                shouldSort: false,
                allowHTML: true,
                searchChoices: this.filterable,
                addItems: this.taggable,
                addChoices: this.taggable, // addItems alone only applies to text inputs; select-multiple/-one also needs this
                removeItemButton: this.clearable,
                noResultsText: this.$_locale('choicesjs.noResultsText'),
                noChoicesText: this.$_locale('choicesjs.noChoicesText'),
                itemSelectText: this.$_locale('choicesjs.itemSelectText'),
                choices: this.buildChoicesArray(),
            }

            if (this.settings) {
                for (let key of Object.keys(this.settings)) {
                    choicesSettings[key] = this.settings[key]
                }
            }

            this.instance = new window.Choices(main, choicesSettings)
        },
        // generated by claude - setChoices() updates the list in place (keeps the dropdown open and
        // the search input focused); destroying and recreating the instance here (the old approach)
        // closed the dropdown on every keystroke during an async search, before the user could click
        // a result.
        updateChoices() {
            if (!this.instance) {
                this.initChoices()
                return;
            }

            this.instance.setChoices(this.buildChoicesArray(), 'value', 'label', true)
        },
        handleValue($event) {
            if (this.multiple) {
                const selectedOptions = Array.from(
                    $event.target.selectedOptions
                )
                .filter((option) => option.value !== '') // the placeholder option, never a real selection
                .map(option => {
                    const [ selected ] = this.displayOptions.filter(row => String(this.optionId(row)) === option.value)

                    // a just-created tag (taggable) isn't in `options` yet - fall back to the raw value
                    return selected ?? (this.taggable ? option.value : null)
                }).filter((option) => option !== null)

                this.$emit('input', selectedOptions)
                return;
            }

            const [ selected ] = this.displayOptions.filter(row => String(this.optionId(row)) === $event.target.value)

            this.$emit('input', selected ?? (this.taggable ? $event.target.value : null))
        },
        getInstance() {
            return this.instance
        },
    },
    mounted() {
        // registered once on the stable <select> element (not inside initChoices(), which only
        // runs once now, but kept separate on principle - a listener here must never be re-added)
        const { main } = this.$refs

        main.addEventListener('search', (event) => {
            this.$emit('search', event.detail.value, (state) => { this.loading = state })
        })

        main.addEventListener('addItem', (event) => {
            if (!this.taggable) return

            const { value } = event.detail
            const alreadyKnown = this.options.some((option) => this.optionId(option) == value)

            if (!alreadyKnown) {
                this.$emit('option:created', value)
            }
        })

        this.$nextTick(() => this.initChoices())
    },
}
</script>
